// ACCTOO Permissions Service
// Requests per-plugin permissions safely and returns a normalized map

import { Camera } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';
import { Geolocation } from '@capacitor/geolocation';
import { PushNotifications } from '@capacitor/push-notifications';

// Check if we're in a Capacitor environment
const isCapacitor = typeof window !== 'undefined' && window.Capacitor;

// Web Browser Permission APIs
const requestWebCameraPermission = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    // Stop the stream immediately as we just needed permission
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    console.error('Camera permission denied:', error);
    return false;
  }
};

const requestWebLocationPermission = async () => {
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      });
    });
    return true;
  } catch (error) {
    console.error('Location permission denied:', error);
    return false;
  }
};

const requestWebNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      return false;
    }
    
    if (Notification.permission === 'granted') {
      return true;
    }
    
    if (Notification.permission === 'denied') {
      return false;
    }
    
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Notification permission denied:', error);
    return false;
  }
};

export async function requestSelectedPermissions(selected = {}) {
  const results = {};

  if (selected.camera) {
    try {
      if (isCapacitor) {
        // Use Capacitor for mobile
        const res = await Camera.requestPermissions();
        results.camera = res?.camera === 'granted';
      } else {
        // Use Web API for browser
        results.camera = await requestWebCameraPermission();
      }
    } catch (error) {
      console.error('Camera permission error:', error);
      results.camera = false;
    }
  }

  if (selected.photos || selected.storage) {
    try {
      if (isCapacitor) {
        // Use Capacitor for mobile
        const res = await Filesystem.requestPermissions();
        results.storage = !!(res?.publicStorage === 'granted' || res?.photos === 'granted');
      } else {
        // For web, storage permission is implicit with file input
        results.storage = true;
      }
    } catch (error) {
      console.error('Storage permission error:', error);
      results.storage = false;
    }
  }

  if (selected.location) {
    try {
      if (isCapacitor) {
        // Use Capacitor for mobile
        const res = await Geolocation.requestPermissions();
        results.location = res?.location === 'granted';
      } else {
        // Use Web API for browser
        results.location = await requestWebLocationPermission();
      }
    } catch (error) {
      console.error('Location permission error:', error);
      results.location = false;
    }
  }

  if (selected.notifications) {
    try {
      if (isCapacitor) {
        // Use Capacitor for mobile
        const res = await PushNotifications.requestPermissions();
        results.notifications = res?.receive === 'granted';
      } else {
        // Use Web API for browser
        results.notifications = await requestWebNotificationPermission();
      }
    } catch (error) {
      console.error('Notification permission error:', error);
      results.notifications = false;
    }
  }

  if (selected.bluetooth) {
    try {
      if (isCapacitor) {
        // Use Capacitor for mobile
        const mod = await import('@capacitor/bluetooth-le').catch(() => null);
        const api = mod && mod.BluetoothLe;
        if (api && api.requestLEScanPermissions) {
          await api.requestLEScanPermissions();
          results.bluetooth = true;
        } else {
          results.bluetooth = false;
        }
      } else {
        // Web Bluetooth API
        if ('bluetooth' in navigator) {
          try {
            const device = await navigator.bluetooth.requestDevice({
              acceptAllDevices: true,
              optionalServices: []
            });
            results.bluetooth = !!device;
          } catch (error) {
            results.bluetooth = false;
          }
        } else {
          results.bluetooth = false;
        }
      }
    } catch (error) {
      console.error('Bluetooth permission error:', error);
      results.bluetooth = false;
    }
  }

  // Normalize any non-selected keys to false for clarity
  const all = { camera: false, storage: false, location: false, notifications: false, bluetooth: false, ...results };
  return all;
}

